import { useCallback, useMemo, useState } from "react";
import { attachmentDisplayClassifier } from "@/lib/AttachmentDisplayClassifier";
import { AttachmentDownloadCache } from "@/lib/AttachmentDownloadCache";

const cacheSingleton = new AttachmentDownloadCache();

function iconForKind(kind) {
  if (kind === "image") return "ri-image-line";
  if (kind === "pdf") return "ri-file-pdf-line";
  if (kind === "document") return "ri-file-word-line";
  if (kind === "text") return "ri-file-text-line";
  return "ri-attachment-2";
}

/**
 * @param {{
 *   attachments?: Array<{ id: string, file_name?: string, mime_type?: string, file_size?: number }>,
 *   fetchDownloadUrl: (attachmentId: string) => Promise<string>,
 *   variant?: 'user' | 'agent' | 'ticket',
 * }} props
 */
export default function MessageAttachments({ attachments, fetchDownloadUrl, variant = "user" }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const items = useMemo(
    () => (Array.isArray(attachments) ? attachments.filter((a) => a?.id) : []),
    [attachments],
  );

  const openAttachment = useCallback(
    async (att) => {
      setError(null);
      setBusyId(att.id);
      try {
        const url = await cacheSingleton.getUrl(att.id, () => fetchDownloadUrl(att.id));
        const kind = attachmentDisplayClassifier.classify(att);
        if (kind === "image") {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.download = att.file_name || "download";
          a.click();
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not open file.");
      } finally {
        setBusyId(null);
      }
    },
    [fetchDownloadUrl],
  );

  if (!items.length) return null;

  const isAgent = variant === "agent";

  return (
    <div className={`mt-2 flex flex-col gap-1.5 ${isAgent ? "items-end" : "items-start"}`}>
      {error ? (
        <p className="text-[11px] text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
      {items.map((att) => {
        const kind = attachmentDisplayClassifier.classify(att);
        const label = att.file_name || "Attachment";
        const size = attachmentDisplayClassifier.formatFileSize(att.file_size);
        const busy = busyId === att.id;

        if (kind === "image") {
          return (
            <button
              key={att.id}
              type="button"
              disabled={busy}
              onClick={() => void openAttachment(att)}
              className="block max-w-[220px] overflow-hidden rounded-lg ring-1 ring-slate-200/80 transition hover:ring-teal-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              aria-label={`Open image ${label}`}
            >
              <span className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 text-left text-[11px] font-medium text-slate-600">
                <i className={`${iconForKind(kind)} text-base text-teal-600`} aria-hidden />
                {label}
                <span className="text-slate-400">({size})</span>
              </span>
            </button>
          );
        }

        return (
          <button
            key={att.id}
            type="button"
            disabled={busy}
            onClick={() => void openAttachment(att)}
            className={`inline-flex max-w-[min(100%,280px)] items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-50 ${
              isAgent
                ? "border-teal-600/40 bg-teal-800/30 text-white hover:bg-teal-800/50"
                : "border-slate-200 bg-white text-slate-800 hover:border-teal-300"
            }`}
            aria-label={`Download ${label}`}
          >
            <i className={`${iconForKind(kind)} shrink-0 text-lg ${isAgent ? "text-teal-200" : "text-teal-600"}`} aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{label}</span>
              <span className={`block text-[10px] ${isAgent ? "text-teal-100/80" : "text-slate-500"}`}>{size}</span>
            </span>
            <i className={`ri-download-2-line shrink-0 ${busy ? "animate-pulse" : ""}`} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
